import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import React from 'react';

import { componentTokens, getButtonColors, shadows,transitions } from '../KestrixDesignSystem.jsx';

/**
 * Kestrix Primary Button - Main action button
 */
const KestrixPrimaryButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("primary");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: `1px solid ${buttonColors.border}`,
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    cursor: "pointer",
    transition: transitions.base,
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: "none", // No shadow for primary button
    "&:hover": {
      background: buttonColors.hover,
      boxShadow: "none",
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: `1px solid ${buttonColors.disabled.border}`,
      boxShadow: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
    "&:active": {
      background: `${buttonColors.background} !important`,
      color: `${buttonColors.color} !important`,
      border: `1px solid ${buttonColors.border} !important`,
    },
  };
});

/**
 * Kestrix Default Button - Outline button for series of actions
 */
const KestrixDefaultButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("default");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: `1px solid ${buttonColors.border}`,
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: buttonColors.shadow,
    "&:hover": {
      background: buttonColors.hover,
      boxShadow: shadows.consistent,
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: `1px solid ${buttonColors.disabled.border}`,
      boxShadow: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
    "&:active": {
      background: buttonColors.active || buttonColors.hover,
      transform: "scale(0.98)",
    },
  };
});

/**
 * Kestrix Dashed Button - For adding more actions
 */
const KestrixDashedButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("dashed");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: `2px dashed ${buttonColors.border}`,
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: buttonColors.shadow,
    "&:hover": {
      background: buttonColors.hover.background,
      color: buttonColors.hover.color,
      border: `2px dashed ${buttonColors.hover.border}`,
      boxShadow: shadows.consistent,
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: `2px dashed ${buttonColors.disabled.border}`,
      boxShadow: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
  };
});

/**
 * Kestrix Text Button - Most secondary action
 */
const KestrixTextButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("text");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: buttonColors.border,
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: buttonColors.shadow,
    "&:hover": {
      background: buttonColors.hover,
      boxShadow: shadows.consistent,
    },
    "&:disabled": {
      background: "transparent",
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: "transparent",
      boxShadow: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
  };
});

/**
 * Kestrix Link Button - For external links
 */
const KestrixLinkButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("link");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: "transparent",
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: buttonColors.shadow,
    textDecoration: buttonColors.textDecoration,
    transition: transitions.base,
    "&:hover": {
      background: "transparent",
      textDecoration: "underline",
      boxShadow: "none",
      opacity: 0.8,
      border: "transparent",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      textDecoration: "underline",
    },
    "&:active": {
      background: "transparent",
      textDecoration: "underline",
      boxShadow: "none",
      opacity: 0.6,
      border: "transparent",
    },
    "&:disabled": {
      background: "transparent",
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: "transparent",
      boxShadow: "none",
      textDecoration: "none",
      opacity: 0.5,
      pointerEvents: "auto",
    },
  };
});

/**
 * Kestrix Danger Button - For risky actions
 */
const KestrixDangerButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("danger");
  const tokens = componentTokens.button;

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: `1px solid ${buttonColors.border}`,
    borderRadius: tokens.borderRadius,
    padding: tokens.padding[size],
    fontWeight: 500,
    fontSize: tokens.fontSize[size],
    textTransform: "none",
    whiteSpace: "nowrap",
    minHeight: tokens.height[size],
    boxShadow: buttonColors.shadow,
    "&:hover": {
      background: buttonColors.hover,
      boxShadow: shadows.consistent,
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: `1px solid ${buttonColors.disabled.border}`,
      boxShadow: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
  };
});

/**
 * Kestrix Icon Button
 */
const KestrixIconButton = styled(Button)(({ size = "medium" }) => {
  const buttonColors = getButtonColors("primary");

  const iconSizes = {
    small: 32,
    medium: 40,
    large: 48,
  };

  return {
    background: buttonColors.background,
    color: buttonColors.color,
    border: "none",
    borderRadius: "50%",
    minWidth: iconSizes[size],
    width: iconSizes[size],
    height: iconSizes[size],
    padding: 0,
    cursor: "pointer",
    transition: transitions.base,
    "&:hover": {
      background: buttonColors.hover,
      transform: "scale(1.05)",
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      transform: "none",
      pointerEvents: "auto",
    },
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
  };
});

/**
 * Kestrix Button component with multiple variants and icon support
 *
 * @param {Object} props - Component props
 * @param {string} [props.variant] - Button variant ('primary', 'default', 'dashed', 'text', 'link', 'danger', 'icon')
 * @param {string} [props.size] - Button size ('small', 'medium', 'large')
 * @param {boolean} [props.fullWidth] - Whether to make the button full width (optional)
 * @param {React.ReactNode} [props.children] - Button content (omit for an icon-only button)
 * @param {React.ReactNode} [props.icon] - Icon component to display
 * @param {string} [props.iconPosition] - Icon position ('start', 'end') - default 'start'
 * @param {Object} [props.sx] - Additional styling
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.disabled] - Whether button is disabled
 * @param {boolean} [props.loading] - Whether button is in loading state
 * @param {string} [props.type] - Button type ('button', 'submit', 'reset')
 * @returns {JSX.Element} Kestrix button component
 */
const KestrixButton = ({
  variant = "primary",
  size = "medium",
  children,
  icon,
  iconPosition = "start",
  sx = {},
  onClick,
  disabled = false,
  loading = false,
  type = "button",
  ...props
}) => {
  const buttonProps = {
    size,
    onClick: loading ? undefined : onClick,
    disabled: disabled || loading,
    type,
    sx,
    ...props,
  };

  // Icon sizes based on button size
  const getIconSize = () => {
    switch (size) {
      case "small":
        return 16;
      case "large":
        return 24;
      case "medium":
      default:
        return 20;
    }
  };

  // Spacing between icon and text
  const getIconSpacing = () => {
    switch (size) {
      case "small":
        return 1;
      case "large":
        return 1.5;
      case "medium":
      default:
        return 1;
    }
  };

  const renderIcon = () => {
    if (!icon) return null;

    return (
      <Box
        component='span'
        sx={{
          fontSize: getIconSize(),
          display: "flex",
          alignItems: "center",
          lineHeight: 0,
        }}
      >
        {React.cloneElement(icon, {
          style: {
            fontSize: getIconSize(),
            ...icon.props?.style,
          },
        })}
      </Box>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: getIconSpacing() }}
        >
          <CircularProgress size={getIconSize()} sx={{ color: "inherit" }} />
          {children}
        </Box>
      );
    }

    // For icon-only buttons (no text)
    if (variant === "icon") {
      return icon || children;
    }

    // Handle icon positioning
    if (icon && children) {
      const iconElement = renderIcon();
      const spacing = getIconSpacing();

      if (iconPosition === "end") {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: spacing }}>
            {children}
            {iconElement}
          </Box>
        );
      } else {
        // Default to 'start'
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: spacing }}>
            {iconElement}
            {children}
          </Box>
        );
      }
    }

    // Just icon or just text
    return icon ? renderIcon() : children;
  };

  switch (variant) {
    case "default":
      return (
        <KestrixDefaultButton variant='contained' {...buttonProps}>
          {renderContent()}
        </KestrixDefaultButton>
      );
      case "default-error":
        return (
          <KestrixDefaultButton variant='contained' {...buttonProps} sx={{color: 'red', borderColor: 'red'}}>
            {renderContent()}
          </KestrixDefaultButton>
        );
    case "dashed":
      return (
        <KestrixDashedButton variant='contained' {...buttonProps}>
          {renderContent()}
        </KestrixDashedButton>
      );
    case "text":
      return (
        <KestrixTextButton variant='contained' {...buttonProps}>
          {renderContent()}
        </KestrixTextButton>
      );
    case "link":
      return (
        <KestrixLinkButton variant='text' {...buttonProps}>
          {renderContent()}
        </KestrixLinkButton>
      );
    case "danger":
      return (
        <KestrixDangerButton variant='contained' {...buttonProps}>
          {renderContent()}
        </KestrixDangerButton>
      );
    case "icon":
      return (
        <KestrixIconButton {...buttonProps}>
          {renderContent()}
        </KestrixIconButton>
      );
    case "primary":
    default:
      return (
        <KestrixPrimaryButton variant='contained' {...buttonProps}>
          {renderContent()}
        </KestrixPrimaryButton>
      );
  }
};

export default KestrixButton;
